import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface DropdownOption<T = string> {
  value: T;
  label: string;
  icon?: ReactNode;
  color?: string; // Tailwind classes for badge styling
  disabled?: boolean;
}

interface DropdownProps<T = string> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: DropdownOption<T>, isSelected: boolean) => ReactNode;
  renderSelected?: (option: DropdownOption<T> | undefined) => ReactNode;
  showCheckmark?: boolean;
  menuPosition?: 'left' | 'right';
  menuWidth?: 'auto' | 'full' | string;
}

function Dropdown<T = string>({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  disabled = false,
  className = '',
  renderOption,
  renderSelected,
  showCheckmark = true,
  menuPosition = 'left',
  menuWidth = 'full',
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = (option: DropdownOption<T>) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
  };

  const getMenuWidthClass = () => {
    if (menuWidth === 'full') return 'left-0 right-0';
    if (menuWidth === 'auto') return menuPosition === 'left' ? 'left-0' : 'right-0';
    return menuPosition === 'left' ? 'left-0' : 'right-0';
  };

  const defaultRenderSelected = (option: DropdownOption<T> | undefined) => {
    if (!option) {
      return <span className="dark:text-gray-400 text-gray-500">{placeholder}</span>;
    }

    if (option.color) {
      return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${option.color}`}>
          {option.icon}
          {option.label}
        </span>
      );
    }

    return (
      <span className="flex items-center gap-2 dark:text-white text-gray-900">
        {option.icon}
        {option.label}
      </span>
    );
  };

  const defaultRenderOption = (option: DropdownOption<T>, isSelected: boolean) => {
    if (option.color) {
      return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${option.color}`}>
          {option.icon}
          {option.label}
        </span>
      );
    }

    return (
      <span className="flex items-center gap-2 dark:text-white text-gray-900">
        {option.icon}
        {option.label}
      </span>
    );
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 rounded-lg border 
            dark:border-gray-600 border-gray-300 
            dark:bg-gray-800 bg-white 
            focus:ring-2 focus:ring-primary focus:border-transparent 
            transition-all flex items-center justify-between 
            hover:border-primary/50
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {renderSelected 
              ? renderSelected(selectedOption) 
              : defaultRenderSelected(selectedOption)
            }
          </div>
          <ChevronDown 
            className={`
              w-5 h-5 dark:text-gray-400 text-gray-500 flex-shrink-0 ml-2
              transition-transform duration-200 
              ${isOpen ? 'rotate-180' : ''}
            `} 
          />
        </button>
        
        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className={`
              absolute top-full mt-2 rounded-xl shadow-lg 
              dark:bg-gray-800 bg-white 
              border dark:border-gray-700 border-gray-200 
              z-50 overflow-hidden 
              animate-in fade-in slide-in-from-top-2 duration-200
              ${getMenuWidthClass()}
              ${menuWidth !== 'full' && menuWidth !== 'auto' ? menuWidth : ''}
            `}
            style={menuWidth !== 'full' && menuWidth !== 'auto' ? { minWidth: menuWidth } : undefined}
          >
            <div className="py-1 max-h-64 overflow-y-auto">
              {options.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    disabled={option.disabled}
                    className={`
                      w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left
                      ${option.disabled 
                        ? 'opacity-50 cursor-not-allowed' 
                        : isSelected 
                          ? 'bg-primary/10 dark:bg-primary/20' 
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <div className="flex-1 min-w-0">
                      {renderOption 
                        ? renderOption(option, isSelected) 
                        : defaultRenderOption(option, isSelected)
                      }
                    </div>
                    {showCheckmark && isSelected && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dropdown;
